import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Typography, Divider, Avatar, Tabs, Tab, Tooltip, Button, Dialog, DialogTitle, DialogContent, Select, MenuItem, FormControl, InputLabel, Alert, IconButton, TextField, Snackbar } from "@mui/material";
import {
  getProfile,
  refreshProfile,
  profileBasics,
  profileWork,
  profileEducation,
  profileRecommendations,
  profileSwot,
  profileMetrics,
} from '../../services/searchService';
import { getLists, addProfileToList, getContactsByLinkedinUsername, createList } from '../../services/userService';

import LinkedInIcon from '@mui/icons-material/LinkedIn';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import TwitterIcon from '@mui/icons-material/Twitter';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import GitHubIcon from '@mui/icons-material/GitHub';
import LanguageIcon from '@mui/icons-material/Language';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import BusinessIcon from '@mui/icons-material/Business';
import StarIcon from '@mui/icons-material/Star';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import GoogleIcon from '@mui/icons-material/Google';
import EmailIcon from '@mui/icons-material/Email';
import CloseIcon from '@mui/icons-material/Close';
import FormatListBulletedAddIcon from '@mui/icons-material/FormatListBulletedAdd';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import Skills from './Skills.js';
import Summary from './Summary.js';
import Academics from './Academics.js';
import Experiences from './Experiences.js';
import Progression from './Progression.js';
import Metrics from './Metrics.js';
import Swot from './Swot.js';
import Assessment from './Assessment.js';
import { pictureUrlToString } from '../../utils/pictureUrl';
// Helper function to capitalize first letter of each word
const capitalizeWords = (str) => {
  if (!str) return '';
  
  // If str is an array, return the first element
  if (Array.isArray(str)) {
    return str[0] || '';
  }
  
  // If str is a string, capitalize words as before
  return str.split(' ').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  ).join(' ');
};

// Helper function to get social media icon
const getSocialIcon = (network) => {
  const networkLower = network.toLowerCase();
  switch (networkLower) {
    case 'linkedin':
      return <LinkedInIcon sx={{ fontSize: '25px', color: '#0077b5' }} />;
    case 'twitter':
      return <TwitterIcon sx={{ fontSize: '25px', color: '#1DA1F2' }} />;
    case 'facebook':
      return <FacebookIcon sx={{ fontSize: '25px', color: '#4267B2' }} />;
    case 'instagram':
      return <InstagramIcon sx={{ fontSize: '25px', color: '#E4405F' }} />;
    case 'github':
      return <GitHubIcon sx={{ fontSize: '25px', color: '#333' }} />;
    case 'gravatar':
      return <AccountCircleIcon sx={{ fontSize: '25px', color: '#1e1e1e' }} />;
    case 'angellist':
    case 'angel':
      return <BusinessIcon sx={{ fontSize: '25px', color: '#000000' }} />;
    case 'klout':
      return <StarIcon sx={{ fontSize: '25px', color: '#ff6b35' }} />;
    case 'foursquare':
      return <LocationOnIcon sx={{ fontSize: '25px', color: '#f94877' }} />;
    case 'aboutme':
    case 'about.me':
      return <PersonIcon sx={{ fontSize: '25px', color: '#00405d' }} />;
    case 'google':
    case 'google+':
      return <GoogleIcon sx={{ fontSize: '25px', color: '#4285f4' }} />;
    default:
      return <LanguageIcon sx={{ fontSize: '25px', color: '#666' }} />;
  }
};

// Helper function to get social media URL
const getSocialUrl = (network, username) => {
  const networkLower = network.toLowerCase();
  switch (networkLower) {
    case 'linkedin':
      return `https://linkedin.com/in/${username}`;
    case 'twitter':
      return `https://twitter.com/${username}`;
    case 'facebook':
      return `https://facebook.com/${username}`;
    case 'instagram':
      return `https://instagram.com/${username}`;
    case 'github':
      return `https://github.com/${username}`;
    case 'gravatar':
      return `https://gravatar.com/${username}`;
    case 'angellist':
    case 'angel':
      return `https://angel.co/${username}`;
    case 'klout':
      return `https://klout.com/${username}`;
    case 'foursquare':
      return `https://foursquare.com/${username}`;
    case 'aboutme':
    case 'about.me':
      return `https://about.me/${username}`;
    case 'google':
    case 'google+':
      return `https://plus.google.com/${username}`;
    default:
      return `https://${username}`;
  }
};

const normalizeProfileData = (rawProfile = {}, fallback = {}) => {
  const socialProfiles = Array.isArray(rawProfile.socialProfiles) ? rawProfile.socialProfiles : [];
  const linkedinProfile = socialProfiles.find((profile) => profile?.network?.toLowerCase() === 'linkedin');

  const linkedinUsernameFromUrl = linkedinProfile?.url
    ? linkedinProfile.url.split('/in/')[1]?.replace(/^\/+|\/+$/g, '')
    : '';

  const linkedinUsername =
    rawProfile.linkedinUsername ||
    linkedinProfile?.username ||
    linkedinUsernameFromUrl ||
    fallback.linkedinUsername ||
    '';

  const linkedinLink =
    rawProfile.linkedinLink ||
    (linkedinProfile?.url
      ? `https://${linkedinProfile.url.replace(/^https?:\/\//, '')}`
      : (linkedinUsername ? `https://www.linkedin.com/in/${linkedinUsername}` : fallback.linkedinLink));

  return {
    ...fallback,
    ...rawProfile,
    socialProfiles,
    linkedinUsername,
    linkedinLink,
  };
};

const normalizeSwotData = (rawSwot = {}) => {
  if (!rawSwot) return null;

  if (rawSwot.swotAnalysis) {
    return rawSwot;
  }

  const hasDirectSwotFields =
    rawSwot.strengths || rawSwot.weaknesses || rawSwot.opportunities || rawSwot.threats;

  if (!hasDirectSwotFields) {
    return null;
  }

  return {
    swotAnalysis: {
      strengths: rawSwot.strengths || '',
      weaknesses: rawSwot.weaknesses || '',
      opportunities: rawSwot.opportunities || '',
      threats: rawSwot.threats || '',
    },
  };
};

function ProfileView({ profileData, publicView }) {
  const [loading, setLoading] = useState(false);
  const [loadingBasic, setLoadingBasic] = useState(false);
  const [loadingWorkExperiences, setLoadingWorkExperiences] = useState(false);
  const [loadingAcademics, setLoadingAcademics] = useState(false);
  const [, setLoadingRoleRecommendations] = useState(false);
  const [, setLoadingSwot] = useState(false);
  const [, setLoadingMetrics] = useState(false);
  // ---- final data ----
  const [profile, setProfile] = useState(normalizeProfileData(profileData, profileData));
  const [profileBasic, setProfileBasic] = useState(normalizeProfileData(profileData, profileData));
  const [profileWorkExperiences, setProfileWorkExperiences] = useState('');
  const [profileAcademics, setProfileAcademics] = useState('');
  const [swotData, setSwotData] = useState('');
  const [roleRecommendations, setRoleRecommendations] = useState('');
  const [metricsData, setMetricsData] = useState('');
  // const [profileBasic, setProfileBasic] = useState(profileData);
  // ---- final data ----
  
  const [tabIndex, setTabIndex] = useState(0);
  const [showAddToListDialog, setShowAddToListDialog] = useState(false);
  const [lists, setLists] = useState([]);
  const [selectedList, setSelectedList] = useState('');
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [contacts, setContacts] = useState({});
  const [contactsLoading, setContactsLoading] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [newListDescription, setNewListDescription] = useState('');
  const [showCreateListForm, setShowCreateListForm] = useState(false);

  const [shareLink, setShareLink] = useState("");
const [openSnackbar, setOpenSnackbar] = useState(false);
  // Get user info from localStorage for credit check
  const userInfo = typeof window !== 'undefined' ? localStorage.getItem('userInfo') : null;
  const user = userInfo ? JSON.parse(userInfo) : null;
  const remainingCredits = user?.remainingCredits || 0;
  const encodedLink = btoa(profileData.linkedinLink);

  useEffect(() => {
    let isMounted = true;
    async function fetchProfile() {
      if (profileData && profileData.linkedinLink) {
        setLoading(true);
        setLoadingBasic(true);
        setLoadingWorkExperiences(true);
        setLoadingAcademics(true);
        setLoadingSwot(true);
        setLoadingRoleRecommendations(true);
        setLoadingMetrics(true);

        try {
          // old method of getting profile data
          const getProfileRaw = await getProfile(encodedLink);
          if (getProfileRaw.status === 'success') {
            const fetchedProfile = normalizeProfileData(getProfileRaw?.data, profileData);
            if (isMounted) {
              setProfile(fetchedProfile);
              setProfileBasic(fetchedProfile);
            }
            // if (isMounted) {
            //   const profileData = getProfileRaw.data;
            //   setProfile(profileData);
            //   setProfileBasic(profileData);
            //   setProfileWorkExperiences(profileData.workExperiences || {});
            //   setProfileAcademics(profileData.academicCredentials || []);
            //   setSkillsData({ technicalSkills: profileData.skills || [] });
            //   setMetricsData({
            //     stability: profileData.stability,
            //     mobility: profileData.mobility
            //   });
            //   if (profileData.swotAnalysis || profileData.swot) {
            //     setSwotData({ swotAnalysis: profileData.swotAnalysis || profileData.swot });
            //   }
            //   if (profileData.roleRecommendations) {
            //     setRoleRecommendations({ roleRecommendations: profileData.roleRecommendations });
            //   }
            // }
            // old method of getting profile data

            // new method of getting profile data
            // profile basics
            profileBasics(encodedLink)
              .then((res) => res.data)
              .then((data) => setProfileBasic(normalizeProfileData(data, fetchedProfile)))
              .catch(() => {})
              .finally(() => setLoadingBasic(false));
            // work experiences
            profileWork(encodedLink)
              .then((res) => res.data)
              .then((data) => setProfileWorkExperiences(data))
              .catch(() => {})
              .finally(() => setLoadingWorkExperiences(false));
            // // academics
            profileEducation(encodedLink)
              .then((res) => res.data)
              .then((data) => setProfileAcademics(data))
              .catch(() => {})
              .finally(() => setLoadingAcademics(false));
            // swot
            profileSwot(encodedLink)
              .then((res) => res.data)
              .then((data) => setSwotData(normalizeSwotData(data)))
              .catch(() => {})
              .finally(() => setLoadingSwot(false));
            // role recommendations
            profileRecommendations(encodedLink)
              .then((res) => res.data)
              .then((data) => setRoleRecommendations(data))
              .catch(() => {})
              .finally(() => setLoadingRoleRecommendations(false));
            // metrics
            profileMetrics(encodedLink)
              .then((res) => res.data)
              .then((data) => setMetricsData(data))
              .catch(() => {})
              .finally(() => setLoadingMetrics(false));
          }
          
        } catch (err) {
          // Optionally handle error
        } finally {
          if (isMounted) setLoading(false);
        }
      }
    }
    fetchProfile();
    return () => { isMounted = false; };
  }, [profileData?.linkedinLink]); // Only depend on the linkedinLink, not the entire profileData object

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
          <Typography variant="h6" sx={{ color: '#666', mb: 2 }}>Hold on! Analyzing openweb and baking insights ...</Typography>
          <CircularProgress size={60} />
        </Box>
      </Box>
    );
  }

  if (!profile) return null;
  
  // Tab state
  const handleRefreshProfile = async (profileLink) => {
    setLoading(true);
    alert(profileLink);
    const encodedLink = btoa(profileLink);
    const data = await refreshProfile(encodedLink);
      setProfile(normalizeProfileData(data.data, profile));
      setLoading(false);
  }

  const handleTabChange = (event, newValue) => {
    event.preventDefault();
    setTabIndex(newValue);
  };

  const handleAddToList = async () => {
    setShowAddToListDialog(true);
    // get lists of the user
    try {
      const listsResponse = await getLists();
      if (listsResponse && listsResponse.status === 'success') {
        setLists(listsResponse.data || []);
        // If no lists exist, show create list form
        if (listsResponse.data.length === 0) {
          setShowCreateListForm(true);
        }
      } else if (Array.isArray(listsResponse)) {
        setLists(listsResponse);
        if (listsResponse.length === 0) {
          setShowCreateListForm(true);
        }
      } else {
        setLists([]);
        setShowCreateListForm(true);
      }
    } catch (error) {
      console.error('Failed to fetch lists:', error);
      // 401 errors are handled by API interceptor (auto redirect to login)
      // For other errors, allow user to create a list
      if (error.response && error.response.status !== 401) {
        setLists([]);
        setShowCreateListForm(true);
      }
    }
  };

  const handleCreateList = async () => {
    if (!newListName.trim()) {
      alert('Please enter a list name');
      return;
    }

    try {
      const response = await createList({ name: newListName, description: newListDescription });
      if (response.status === 'success') {
        // Refresh lists after creation
        const listsResponse = await getLists();
        if (listsResponse.status === 'success') {
          setLists(listsResponse.data);
          // Find the newly created list and add the profile to it
          const newList = listsResponse.data.find(list => list.name === newListName);
          if (newList) {
            try {
              await addProfileToList(newList._id, profile.linkedinUsername);
              setShowSuccessAlert(true);
              // Show the lists dropdown after successful creation
              setShowCreateListForm(false);
              setNewListName('');
              setNewListDescription('');
              setSelectedList('');
              setTimeout(() => {
                setShowSuccessAlert(false);
              }, 2000);
            } catch (error) {
              console.error('Error adding profile to new list:', error);
              alert('List created but failed to add profile. Please try adding manually.');
              // Still show the lists dropdown even if adding profile failed
              setShowCreateListForm(false);
              setNewListName('');
              setNewListDescription('');
              setSelectedList('');
            }
          }
        }
      } else {
        alert('Error creating list: ' + response.message);
      }
    } catch (error) {
      alert('Error creating list: ' + error.message);
    }
  };

  const handleGetContacts = async () => {
    try {
      // TODO: Replace with actual API call to get contact details
      // const response = await getContactDetails(profile.linkedinLink);
      setContactsLoading(true);
      const contactsResponse = await getContactsByLinkedinUsername(profile.linkedinUsername);
      setContacts(contactsResponse.data);
      setContactsLoading(false);
      // For now, just show an alert
      // alert('Contact details API call would be made here for: ' + profile.linkedinLink);
    } catch (error) {
      console.error('Error getting contacts:', error);
    } finally {
      setContactsLoading(false);
    }
  };
const handleShareProfile = async () => {
  try {
    const res = await fetch("/api/profile-share/share-profile", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ profileData: profile })
    });

    if (!res.ok) {
      throw new Error("Failed to generate share link");
    }

    const data = await res.json();

    if (!data.token) {
      throw new Error("No token received");
    }

    const link = `${window.location.origin}/public/profile/${data.token}`;
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      console.warn("Clipboard failed, user can copy manually");
    }
    setShareLink(link);
    setOpenSnackbar(true);

  } catch (err) {
    console.error("Error sharing profile:", err);
  }
};
  const loadingComponent = () => {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress size={20} />
      </Box>
    )
  }
    return (
        <>
            {/* Profile Details */}
            <Box sx={{ width: '100%', bgcolor: '#fff', boxShadow: 1, borderRadius: 1, p:2, mb:0.5, display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 1 }}>
                {/* Profile Image and Social Profiles */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ minHeight: 100, maxHeight: 150, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 1, borderRadius: 20, bgcolor: '#fff'}}>
                        <Avatar src={pictureUrlToString(profile.profilePicture)} alt={profile.firstName + ' ' + profile.lastName} sx={{ width: 150, height: 150, border: '1px solid #e0e0e0' }} />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems:'center', justifyContent:'center' }}>
                      <Box sx={{ mt: 1, display: 'flex', gap: 2 }}>
                        {/* add to list */}
                         <Tooltip title={remainingCredits === 0 ? "You used all your credits!" : "Refresh Profile"}>
                          <RefreshIcon 
                            sx={{ 
                              color: remainingCredits === 0 ? '#ccc' : '#1BE885', 
                              cursor: remainingCredits === 0 ? 'not-allowed' : 'pointer', 
                              fontSize: '2.5rem' , 
                              marginLeft: '10px', marginRight: '0px', 
                              border: `1px solid ${remainingCredits === 0 ? '#ccc' : '#1BE885'}`,
                              borderRadius: '2px',
                              backgroundColor: '#fff',
                            }} 
                            onClick={() => {
                              if (remainingCredits > 0) {
                                handleRefreshProfile(`${profile.linkedinLink}`);
                              }
                            }} 
                            titleAccess={remainingCredits === 0 ? "You used all your credits!" : "Refresh Profile"} 
                          />
                        </Tooltip> 
                        <Tooltip title={remainingCredits === 0 ? "You used all your credits!" : "Add to List"}>
                          <button
                            onClick={() => {
                              if (remainingCredits > 0) {
                                handleAddToList();
                              }
                            }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              // padding: '5px 10px',
                              border: `1px solid ${remainingCredits === 0 ? '#ccc' : '#1BE885'}`,
                              borderRadius: '2px',
                              backgroundColor: '#fff',
                              color: remainingCredits === 0 ? '#ccc' : '#1BE885',
                              cursor: remainingCredits === 0 ? 'not-allowed' : 'pointer',
                              marginLeft: '10px',
                              marginRight: '0px',
                              transition: 'all 0.2s ease',
                              fontSize: '2.5rem',
                              fontWeight: '500'
                            }}
                          >
                            <FormatListBulletedAddIcon sx={{ fontSize: '1.5rem' }} titleAccess={remainingCredits === 0 ? "You used all your credits!" : "Add to List"} />
                          </button>
                        </Tooltip>
                      </Box>
                    </Box>
                </Box>
                {/* Divider 1 */}
                <Divider orientation="vertical" sx={{ mr: 2, ml: 2 }} flexItem />
                {/* Profile Details */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center', justifyContent: 'flex-start' }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>{capitalizeWords(profile.firstName)} {capitalizeWords(profile.lastName)}</Typography> 
                        
                        <Divider orientation="vertical" sx={{ ml: 1 }} flexItem />
                        {/* Social Profiles */}
                        {profile.socialProfiles && profile.socialProfiles.length > 0 && (
                        <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'flex-start' }}>
                            {(() => {
                                const seenNetworks = new Set();
                                const uniqueProfiles = profile.socialProfiles.filter(socialProfile => {
                                    const networkLower = socialProfile.network.toLowerCase();
                                    if (seenNetworks.has(networkLower)) {
                                        return false;
                                    }
                                    seenNetworks.add(networkLower);
                                    return true;
                                });
                  
                                return uniqueProfiles.map((socialProfile, index) => (
                                    <a
                                        key={index}
                                        href={
                                          socialProfile.url
                                            ? `https://${socialProfile.url.replace(/^https?:\/\//, '')}`
                                            : getSocialUrl(socialProfile.network, socialProfile.username || socialProfile.network)
                                        }
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ textDecoration: 'none' }}
                                    >
                                        <Tooltip title={`${socialProfile.network}`}>
                                            <IconButton   
                                                size={'large'}
                                                // sx={{ size: 'large' }}
                                        >
                                            
                                            {getSocialIcon(socialProfile.network)}
                                        </IconButton>
                                        </Tooltip>
                                    </a>
                                ));
                            })()}
                        </Box>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', flexDirection: 'row', alignItems: 'left', justifyContent: 'left', gap: 1, mt: 1 }}>
                        
                        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                            {/* {profile.snippet ? ( */}
                              <Typography variant="subtitle1" sx={{ color: '#666', mb: 0.5 }}>{profile.snippet || (profile.currentPosition && `${capitalizeWords(profile.currentPosition)}${profile.currentOrg ? ` at ${capitalizeWords(profile.currentOrg)}` : ''}`)}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                            {/* // ) : ( */}
                              <Typography variant="subtitle2" sx={{ color: '#666', mb: 0.5 }}>{profile.headline || profile.title}</Typography>
                            {/* // )} */}
                        </Box>
                        
                        {profile.totalExperience && (
                            <Typography variant="subtitle2" sx={{ color: '#888' }}><b>Total Experience:</b> {profile.totalExperience} years</Typography>
                        )}
                        {profile.location && (
                            <Typography variant="subtitle2" sx={{ color: '#888' }}><b>Location:</b> {capitalizeWords(profile.location)}</Typography>
                        )}
                        {profile.industry && (
                            <Typography variant="subtitle2" sx={{ color: '#888' }}><b>Industry:</b> {capitalizeWords(profile.industry)}</Typography>
                        )}
                        {profile.languages && profile.languages.length > 0 && (
                            <Typography variant="subtitle2" sx={{ color: '#888' }}>
                              <b>Languages:</b> {profile.languages.map((lang) => capitalizeWords(lang?.name)).filter(Boolean).join(', ')}
                            </Typography>
                        )}
                    </Box>
                </Box>
                {/* Divider 2 */}
                <Divider orientation="vertical" flexItem />
        
                {/* Actions Section */}
                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: '200px' }}>
                  {/* contacts box */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1}}>
                    {!contactsLoading && !contacts && (
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Typography variant="subtitle2" sx={{ color: '#666' }}>No contacts available</Typography>
                      </Box>
                    )}
                    {!contactsLoading && contacts && !contacts.emails && !publicView && (
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Tooltip 
                          title={remainingCredits === 0 ? "You used all your credits!" : "Get Contacts"}
                          disableInteractive={false}
                        >
                          <span>
                            <Button 
                              variant="outlined" 
                              sx={{ 
                                backgroundColor: remainingCredits === 0 ? '#ccc' : '#2196F3', 
                                color: '#fff' 
                              }} 
                              onClick={() => {
                                if (remainingCredits > 0) {
                                  handleGetContacts();
                                }
                              }} 
                              disabled={profile.isFetched || remainingCredits === 0}
                            >
                              <Typography variant="body2" sx={{ color: '#ffff', fontSize: '0.8rem', mx: 1 }}>Get Contacts</Typography>
                              <ContactPhoneIcon sx={{ fontSize: '1.5rem' }} />
                            </Button>
                          </span>
                        </Tooltip>
                      </Box>
                    )} 
                    {contactsLoading && (
                      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={20} />
                        <Typography variant="body2" sx={{ color: '#666', fontSize: '12px' }}>Loading contact details..</Typography>
                      </Box>
                    )}
                    {!contactsLoading && contacts.emails && contacts.emails.length > 0 && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="subtitle2" sx={{color: '#666', fontWeight: 'bold' }}>Emails:</Typography>
                        {contacts.emails.map((email, index) => (
                          <Box sx={{ display: 'flex', flexDirection: 'row'}} key={index}>
                            <EmailIcon sx={{ fontSize: '16px', color: '#666', marginRight: '5px' }} />
                            <Typography variant="body2" sx={{ color: '#888', fontSize: '11px' }}>{email.address}</Typography>
                          </Box>                      
                        ))}
                      </Box>
                    )}
                    {!contactsLoading && contacts.emails && contacts.emails.length === 0 && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="subtitle2" sx={{ color: '#666' }}>No emails found</Typography>
                      </Box>
                    )}
                    {!contactsLoading && contacts.phoneNumbers && contacts.phoneNumbers.length > 0 && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="subtitle2" sx={{ color: '#666', fontWeight: 'bold' }}>Phone Numbers:</Typography>
                        {contacts.phoneNumbers.map((phone, index) => (
                        <Box sx={{ display: 'flex', flexDirection: 'row'}} key={index}>
                          <LocalPhoneIcon sx={{ fontSize: '16px', color: '#666', marginRight: '5px' }} />
                          <Typography variant="body2" sx={{ color: '#888', fontSize: '11px' }}>
                            {typeof phone === 'object' ? phone.number : phone}
                          </Typography>
                        </Box>                      
                        ))}

                      </Box>
                    )} 
                    {!contactsLoading && contacts.phoneNumbers && contacts.phoneNumbers.length === 0 && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="subtitle2" sx={{ color: '#666' }}>No phone numbers found</Typography>
                      </Box>
                    )}
                  {!publicView && (
                            <>
                              {/* <Button
                                variant="contained"
                                sx={{ mt: 2, backgroundColor: '#1BE885', color: '#fff' }}
                                onClick={handleShareProfile}
                              >
                                Copy Public Shareable Link
                              </Button> */}

                              {/* {shareLink && (
                                <Typography
                                  variant="body2"
                                  sx={{ mt: 1, wordBreak: "break-all", color: "#555" }}
                                >
                                  {shareLink}
                                </Typography>
                              )} */}
                            </>
                          )}
                                              </Box>
                    {/* contacts box */}
                </Box>
            </Box>
            {/* Profile Details */}
            
            {/* Skills */}
            {profile.skills && profile.skills.length > 0 && (
              <Skills skills={profile.skills} />
            )}
            {/* Skills */}
            <Divider orientation="vertical" sx={{ mr: 2, ml: 2 }} flexItem />
            
            {/* Details Section */}
            <Box sx={{ width: '100%', boxShadow: 1, borderRadius: 1, mb:0.5, pb:2, pt:1, bgcolor: '#fff', display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 2 }}>
                {/* Tabs */}
                <Box sx={{ width: '100%', bgcolor: 'background.paper', mb: 2, pd:2 }}>
                    <Tabs value={tabIndex} onChange={handleTabChange} aria-label="profile tabs" sx={{'& .MuiTabs-indicator': { backgroundColor: '#1BE885' } }}>
                        <Tab label="Professional Journey" sx={{ color: '#1BE885', fontWeight: 'bold', '&.Mui-selected': { color: '#1BE885' } }} />
                        <Tab label="Assessment" sx={{ color: '#1BE885', fontWeight: 'bold', '&.Mui-selected': { color: '#1BE885' } }} />
                        <Tab label="Insights" sx={{ color: '#1BE885', fontWeight: 'bold', '&.Mui-selected': { color: '#1BE885' } }} />
                    </Tabs>
                    <Divider sx={{ mb: 2 }} />
                    {tabIndex === 0 && (
                        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {/* profileSummary */}
                            {loadingBasic && ( loadingComponent() )}
                            {(profile.profileSummary || profileBasic?.profileSummary) && (<Summary summary={profile.profileSummary || profileBasic.profileSummary} aspirations={profile.aspirations || profileBasic?.aspirations} />)}
                            {/* profileSummary */}
                            {/* {loadingBasic && ( loadingComponent() )} */}
                            {/* {!loadingBasic && profileBasic && (<Summary summary={profileBasic.profileSummary} aspirations={profileBasic.aspirations} />)} */}
                            {/* workExperience */}
                            <Experiences workExperiences={profileWorkExperiences} capitalizeWords={capitalizeWords} loadingWorkExperiences={loadingWorkExperiences} />
                            {/* workExperience */}
                            <Divider sx={{ mb: 2 }} />
                            {/* academics */}
                            {loadingAcademics && (loadingComponent())}
                            {!loadingAcademics && profileAcademics && (<Academics academics={profileAcademics} capitalizeWords={capitalizeWords} />)}
                            {/* academics */}
                        </Box>
                    )}
                    
                    {tabIndex === 1 && (
                      <Assessment keySkillsAndExpertise={profile.keySkillsAndExpertise} linkedinUsername={profile.linkedinUsername} />
                    )} 

                    {tabIndex === 2 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mx: 2}}>
                            {/* Progression */}
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="h5" sx={{ color: '#666', mb: 2 }}>Progression</Typography>
                                <Progression progression={profileWorkExperiences} capitalizeWords={capitalizeWords} /> 
                            </Box>
                            {/* Stability & Mobility */}
                            <Box sx={{ width: '100%' }}>
                                <Typography variant="h5" sx={{ color: '#666', mb: 2 }}>Career Metrics</Typography>
                                {/* Stability Slider */}
                                <Metrics stability={metricsData.stability} mobility={metricsData.mobility} />
                                <Divider sx={{ mb: 2, mt: 2 }} />
                            </Box>
                            {/* Stability & Mobility */}
                            {/* SWOT */}
                            {(profile.swotAnalysis || swotData?.swotAnalysis) && (
                              <Swot swot={profile.swotAnalysis ? normalizeSwotData({ swotAnalysis: profile.swotAnalysis }) : swotData} />
                            )}
                            {/* SWOT */}
                            <Divider sx={{ mb: 2, mt: 2 }} />
                            {/* Role Recommendations */}
                            {(profile.roleRecommendations || (roleRecommendations && roleRecommendations.roleRecommendations)) && (
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="h5" sx={{ color: '#666', mb: 2 }}>Role Recommendations</Typography>
                                  <Typography variant="body2">{profile.roleRecommendations || (roleRecommendations && roleRecommendations.roleRecommendations)}</Typography>
                                </Box>
                            )}
                            {/* Role Recommendations */}
                        </Box>
                    )}
                </Box>
            </Box>
            <Divider sx={{ mb: 5, mt: 5 }} />
      {/* Skills */}
      
      {/* Add to List Dialog */}
      <Dialog open={showAddToListDialog} onClose={() => setShowAddToListDialog(false)} maxWidth="sm" fullWidth >
        <DialogTitle>Add "{profile.firstName} {profile.lastName}" to a list</DialogTitle>
        <IconButton 
          aria-label="close" 
          sx={{ position: 'absolute', right: 8, top: 8, color: '#fffff' }}
          onClick={() => {
            setShowAddToListDialog(false);
            setSelectedList('');
            setShowSuccessAlert(false);
            setShowCreateListForm(false);
            setNewListName('');
            setNewListDescription('');
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          
          {/* Success Alert */}
          {showSuccessAlert && (
            <Alert severity="success" sx={{ mb: 1 }}>
              {showCreateListForm ? 'List created and profile added successfully!' : 'Profile added to list successfully!'}
            </Alert>
          )}
          
          {/* Create New List Form */}
          {showCreateListForm && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, color: '#666' }}>Create New List</Typography>
              <TextField fullWidth label="List Name" value={newListName} onChange={(e) => setNewListName(e.target.value)} sx={{ mb: 1 }} required />
              <TextField fullWidth label="Description (Optional)" value={newListDescription} onChange={(e) => setNewListDescription(e.target.value)} sx={{ mb: 1 }} />
              <Button 
                variant="contained" 
                onClick={() => {
                  if (remainingCredits > 0) {
                    handleCreateList();
                  }
                }} 
                disabled={remainingCredits === 0}
                sx={{ 
                  mr: 1, 
                  backgroundColor: remainingCredits === 0 ? '#ccc' : '#1BE885', 
                  color: '#fff' 
                }}
                title={remainingCredits === 0 ? "You used all your credits!" : ""}
              >
                Create List
              </Button>
              <Button variant="outlined" onClick={() => {setShowCreateListForm(false); setNewListName(''); setNewListDescription('');}}>Cancel</Button>
            </Box>
          )}
          
          {/* Existing Lists Section */}
          {lists.length > 0 && !showCreateListForm && (
            <Box sx={{ mb: 1 }}>
              <FormControl fullWidth sx={{ mb: 1 }}>
                <InputLabel>Select a list</InputLabel>
                <Select
                  value={selectedList}
                  onChange={(e) => {
                    if (e.target.value === 'create_new') {
                      setShowCreateListForm(true);
                      setSelectedList('');
                    } else {
                      setSelectedList(e.target.value);
                    }
                  }}
                  label="Select a list"
                >
                  {lists.map((list) => (
                    <MenuItem key={list._id} value={list._id}>{list.name}</MenuItem>
                  ))}
                  <Divider sx={{ my: 1 }} />
                  <MenuItem value="create_new" sx={{fontWeight: 'bold' }}>
                    + Create a new list
                  </MenuItem>
                </Select>
              </FormControl>

              {selectedList && (
                <Button
                  variant="contained"
                  disabled={remainingCredits === 0}
                  sx={{ 
                    backgroundColor: remainingCredits === 0 ? '#ccc' : '#1BE885', 
                    color: '#fff' 
                  }}
                  onClick={async () => {
                    if (remainingCredits > 0) {
                      try {
                        await addProfileToList(selectedList, profile.linkedinUsername);
                        setShowSuccessAlert(true);
                        setTimeout(() => {
                          setShowSuccessAlert(false);
                          setShowAddToListDialog(false);
                          setSelectedList('');
                        }, 2000);
                      } catch (error) {
                        console.error('Error adding profile to list:', error);
                      }
                    }
                  }}
                  title={remainingCredits === 0 ? "You used all your credits!" : ""}
                >
                  Add to Selected List
                </Button>
              )}
            </Box>
          )}
        </DialogContent>
  
      </Dialog>
              <Snackbar
          open={openSnackbar}
          autoHideDuration={3000}
          onClose={() => setOpenSnackbar(false)}
          message="Link copied, valid for 7 days"
        />
    </>
  );
}

export default ProfileView;